import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { equal, isAlive } from 'webapp/utils/common';

import type { CustomMetricSetupFormItemGroup } from 'webapp/components/custom-query/setup/prebuilt/group';

import type { PreviewErrorData } from 'webapp/models-custom/custom-metric-setup-form/base';
import CustomMetricSetupFormBase from 'webapp/models-custom/custom-metric-setup-form/base';
import type {
	NameIdPair,
	ValueSelected,
} from 'webapp/models-custom/custom-metric-setup-form/item/field';
import CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type { CustomMetricSetupFormItem } from 'webapp/models-custom/custom-metric-setup-form/item/group/base';
import CustomMetricSetupFormItemGroupCloneable from 'webapp/models-custom/custom-metric-setup-form/item/group/cloneable';
import CustomMetricSetupFormItemGroupCommon from 'webapp/models-custom/custom-metric-setup-form/item/group/common';
import StatusObject from 'webapp/models-custom/status-object';

export default class CustomMetricSetupFormPrebuilt extends CustomMetricSetupFormBase {
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked items: (CustomMetricSetupFormItemGroup | CustomMetricSetupFormItemField)[] = [];
	@tracked valueChangeCounter = 0; // for "watching" for all value changes in components
	@tracked hiddenChangeCounter = 0; // for "watching" for all hidden changes in components

	@tracked override originalSettings: {
		builder: APIData.CustomQuery.Prebuilt.BuilderData;
		settings: APIData.CustomQuery.Prebuilt.SettingsData;
	} | null = null;

	get isWizardFlowSupported(): boolean {
		return false;
	}

	// ------- Initializing --------

	override initData(data: APIData.CustomQuery.BuilderPrebuiltResponse) {
		this.transformsData = data.transformations || null;

		super.initData(data);

		this.initItems();
	}

	private initItems(): void {
		const originalSettings = this.originalSettings;

		if (!originalSettings) {
			return;
		}

		// always add builder type switcher to the fields
		const typeField: APIData.CustomQuery.Prebuilt.BuilderItem = {
			id: '__type',
			name: 'Type',
			type: 'radio',
			size: 100,
			items: originalSettings.builder.map((builderTypeInfo) => {
				return builderTypeInfo.__type;
			}),
		};

		const settings = originalSettings.settings;

		let selectedType: NameIdPair;
		// if it's already selected, add that
		if (settings?.__type) {
			selectedType = settings.__type;
		} else {
			// just select the first one
			selectedType = typeField.items?.[0] as APIData.CustomQuery.Prebuilt.BuilderItemOption;
			if (settings) {
				settings.__type = selectedType;
			}
		}

		typeField.value = selectedType.id;

		if (!this.builderType) {
			this.builderType = selectedType.id;
		}

		const fields: APIData.CustomQuery.Prebuilt.BuilderItem[] = [];

		fields.push(typeField);

		const builderInfo = originalSettings.builder.find(
			(typeInfo) => typeInfo.__type.id === selectedType.id,
		);
		if (!builderInfo) {
			return;
		}
		fields.push(...builderInfo.items);

		const items: (CustomMetricSetupFormItemGroup | CustomMetricSetupFormItemField)[] = [];
		fields.forEach((item) => {
			items.push(this.parseBuilderItem(item, settings));
		});

		this.items = items;

		this.builderTypeChanged();

		next(() => {
			this.lastQueriedPreviewPayload = {
				settings: this.prepareSettings(), // so we can check isDirty
				transformations: null,
			};
		});
	}

	parseBuilderItem(
		item: APIData.CustomQuery.Prebuilt.BuilderItem,
		settings: APIData.CustomQuery.Prebuilt.SettingsData,
	) {
		let itemObject: CustomMetricSetupFormItem;
		if (item.type === 'group') {
			let groupObject:
				CustomMetricSetupFormItemGroupCommon | CustomMetricSetupFormItemGroupCloneable;
			if (item.cloneable) {
				groupObject = new CustomMetricSetupFormItemGroupCloneable(item, this);
			} else {
				groupObject = new CustomMetricSetupFormItemGroupCommon(item, this);
			}
			(item.items || []).forEach((i) => {
				const newItem = this.parseBuilderItem(
					i as APIData.CustomQuery.Prebuilt.BuilderItem,
					settings,
				);
				groupObject.items.push(newItem);
			});
			itemObject = groupObject;
		} else {
			itemObject = new CustomMetricSetupFormItemField(item, this);
		}

		itemObject.setInitialValues(settings);

		return itemObject;
	}

	// ------ current form status & handlers ---------

	@tracked builderType = ''; // the active "__type" (one querybuilder can have multiple)

	get isFormReady(): boolean {
		return this.items.length > 0;
	}

	get isPreviewButtonDisabled(): boolean {
		return !this.items.length;
	}

	override get isSaveButtonDisabled(): boolean {
		return super.isSaveButtonDisabled || this.isPreviewButtonDisabled;
	}

	get builderTypeItem(): CustomMetricSetupFormItemField | null {
		return (this.items[0] as CustomMetricSetupFormItemField) || null;
	}

	prepareSettings(): APIData.CustomQuery.Prebuilt.SettingsData {
		if (this.prepareSettingsFn) {
			// the query's saved settings, of this form's kind
			return this.prepareSettingsFn() as APIData.CustomQuery.Prebuilt.SettingsData;
		}
		const data: APIData.CustomQuery.Prebuilt.SettingsData = {};
		this.items.forEach((item) => {
			if (!item.isHidden) {
				Object.assign(data, item.valueRecord);
			}
		});
		return data;
	}

	// Currently we are checking items with `definesRawId` only on first level of items
	// - NOT supported on group items - https://databox.atlassian.net/l/c/M2CYGP3F#Quick-tips-for-writing-MetricSettings
	checkAreItemsWithDefinesRawIdChanged(): boolean {
		const { items, originalSettings, lastQueriedPreviewPayload } = this;
		const itemsWithDefinesRawId = items.filter((item) => item.definesRawId);

		return itemsWithDefinesRawId.some((i) => {
			const item = i as CustomMetricSetupFormItemField;
			const itemId = item.id;
			const originalSettingsItemValue = originalSettings?.settings[itemId];

			return (
				!equal(
					originalSettingsItemValue,
					(lastQueriedPreviewPayload as APIData.CustomQuery.Prebuilt.PreviewRequest)
						.settings[itemId],
				) && !equal(originalSettingsItemValue, item.value)
			);
		});
	}

	builderTypeChanged(): void {
		const builderType = this.builderTypeItem?.valueFormatted as NameIdPair;

		if (builderType) {
			const originalSettings = this.originalSettings;

			if (!originalSettings) {
				// should not come to this
				return;
			}

			if (builderType.id !== originalSettings?.settings?.__type?.id) {
				// reset settings if we have switched __type
				originalSettings.settings = {
					__type: {
						id: builderType.id,
						name: builderType.name,
					},
				};
				this.originalSettings = originalSettings;
			}
			if (builderType.id !== this.builderType) {
				this.builderType = builderType.id;
				this.initItems();
			}
		}
	}

	updateExternalField(fieldId: string, fieldValue: ValueSelected): void {
		const item = (this.items || []).find(
			(item) => item.id === fieldId,
		) as CustomMetricSetupFormItemField;
		if (item && item.value !== fieldValue) {
			item.value = fieldValue;

			if (fieldId === 'datatype') {
				this.infolayerService.ok('The default datatype has been changed.');
			}
		}
	}

	// -------- form data & preview ---------

	@tracked override lastQueriedPreviewPayload:
		APIData.CustomQuery.Prebuilt.PreviewRequest | Record<string, never> | null = null; // Record<string, never> === {}

	// -------- validation & error handling ---------

	get isValidAndValidated() {
		return !this.items.some((field) => !field.isValidAndValidated);
	}

	get isValidatingWithRequest() {
		return this.items.some((field) => field.isValidatingWithRequest);
	}

	handlePreviewErrorFromStatusObjects(statusObjects: StatusObject[]): void {
		// make a jquery obj bcos we just plop it in the popup (common/popups/popup-api-response)
		const dataErrorEl = StatusObject.getDataErrorText(statusObjects);

		if (dataErrorEl) {
			const actionableRefetchEl = dataErrorEl.find('a.actionable[data-type="refetch"]');
			// we have to remove already bound click events first
			actionableRefetchEl.off('click').on('click', () => {
				void this.triggerRefetchPreview();
				void this.popupService.close();
			});

			this.handlePreviewError({ html: dataErrorEl });
		}
	}

	handlePreviewError(errorData: PreviewErrorData) {
		this.onError(errorData, 'Data preview error');
	}

	validate(): Promise<boolean> {
		const promises = this.items.map((item) => {
			return item.triggerValidate();
		});
		return Promise.allSettled(promises).then(() => this.isValid);
	}

	// -------- field statuses ---------

	@tracked _currentlyFocusedField: CustomMetricSetupFormItemField | null = null;
	get currentlyFocusedField(): CustomMetricSetupFormItemField | null {
		return this._currentlyFocusedField;
	}

	set currentlyFocusedField(field: CustomMetricSetupFormItemField | null) {
		this._currentlyFocusedField = field;
		this.onCurrentlyFocusedFieldChange();
	}

	private unfocusCurrentlyFocusedField(): void {
		const focused = this.currentlyFocusedField;
		if (focused && typeof focused === 'object' && isAlive(focused)) {
			focused.isFocused = false;
		}
	}

	protected onCurrentlyFocusedFieldChange(): void {
		//maybe extended
	}

	handleItemAction(item: CustomMetricSetupFormItemField | string, action: string): void {
		if (action === 'focus-in') {
			this.unfocusCurrentlyFocusedField();
			// item can be also string (f.ex. query name)
			if (typeof item !== 'string') {
				this.currentlyFocusedField = item;
				item.isFocused = true;
			}
		} else if (action === 'focus-out') {
			if (this.currentlyFocusedField === item) {
				this.unfocusCurrentlyFocusedField();
				this.currentlyFocusedField = null;
			}
		} else if (action === 'change') {
			if (item === this.builderTypeItem) {
				this.builderTypeChanged();
			}
			if (typeof item !== 'string' && item.id === 'dimensions') {
				this.checkToResetSelectedPreviewDimension(item);
			}
			this.bumpValueChangeCounter();
		}
	}

	bumpValueChangeCounter(): void {
		this.valueChangeCounter++;
	}

	bumpHiddenChangeCounter(): void {
		this.hiddenChangeCounter++;
	}
}
